/*
 * Dusk & Relic — country / currency selector + geo-IP localization.
 *
 * Scope: the four markets we advertise in — US, CA, GB, AU. Shopify Markets
 * sells to 189 countries, but only these four get a local currency here; the
 * rest keep checking out in USD as they always have.
 *
 *   1. <dnr-loc-form> — opens the panel and submits the localization form.
 *      Written from scratch rather than reusing Dawn's <localization-form>,
 *      which is only loaded on a couple of templates here while the header
 *      lives on all of them.
 *
 *   2. Geo detection — Shopify only auto-assigns a visitor to their own market
 *      on the Plus plan. Everywhere else the theme has to ask for it, through
 *      the Ajax browsing-context endpoint, which reads the visitor's IP and
 *      Accept-Language server-side. We ask once, act once, and remember the
 *      answer: a manual choice is never overridden, and a wrong GeoIP guess is
 *      always one click from being undone.
 */
(function () {
  'use strict';

  var KEY = 'dnr:loc';

  function readState() {
    try {
      return JSON.parse(localStorage.getItem(KEY) || 'null');
    } catch (e) {
      return null;
    }
  }

  function writeState(state) {
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch (e) {
      /* Private mode — detection simply re-runs next visit. */
    }
  }

  /* The rendered selector is the source of truth for what each country costs
     in, so the notice never has to be told separately. */
  function rowFor(code) {
    return document.querySelector('.dnr2-loc__row[data-value="' + code + '"]');
  }

  function currencyFor(code) {
    var row = rowFor(code);
    var cur = row && row.querySelector('.dnr2-loc__cur');
    return cur ? cur.textContent.trim().split(/\s+/)[0] : '';
  }

  function countryNameFor(code) {
    var row = rowFor(code);
    var name = row && row.querySelector('.dnr2-loc__name');
    return name ? name.textContent.trim() : '';
  }

  /* ------------------------------------------------------------------ *
   * 1. The selector
   * ------------------------------------------------------------------ */

  class DnrLocForm extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="country_code"]');
      this.button = this.querySelector('.dnr2-loc__btn');
      this.panel = this.querySelector('.dnr2-loc__panel');
      if (!this.form || !this.input || !this.button || !this.panel) return;

      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggle();
      });

      this.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape') return;
        this.close();
        this.button.focus();
      });

      document.addEventListener('click', (event) => {
        if (this.contains(event.target)) return;
        this.close();
      });

      this.querySelectorAll('.dnr2-loc__row').forEach((row) => {
        row.addEventListener('click', (event) => {
          event.preventDefault();
          this.select(row.getAttribute('data-value'));
        });
      });
    }

    open() {
      this.panel.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
    }

    close() {
      this.panel.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
    }

    toggle() {
      if (this.panel.hidden) this.open();
      else this.close();
    }

    select(code) {
      if (!code) return;
      /* Marked as the visitor's own choice, so detection never overrides it. */
      writeState({ country: code, how: 'user' });
      this.input.value = code;
      this.form.submit();
    }
  }

  if (!window.customElements.get('dnr-loc-form')) {
    window.customElements.define('dnr-loc-form', DnrLocForm);
  }

  /* ------------------------------------------------------------------ *
   * 2. Geo detection
   * ------------------------------------------------------------------ */

  function root() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function openSelector() {
    var el = document.querySelector('.dnr2-loc--header') || document.querySelector('dnr-loc-form');
    if (!el || typeof el.open !== 'function') return;
    el.open();
    var btn = el.querySelector('.dnr2-loc__btn');
    if (btn) btn.focus();
  }

  function fill(template, code) {
    return (template || '')
      .replace('[currency]', currencyFor(code))
      .replace('[country]', countryNameFor(code));
  }

  /*
   * One quiet bar, bottom-left. `onAccept` is what separates the two modes:
   * in ask mode it performs the switch, in auto mode the switch already
   * happened and the button just opens the selector so it can be reversed.
   */
  function showNotice(text, ctaLabel, onAccept) {
    if (!text) return;

    var bar = document.createElement('div');
    bar.className = 'dnr2-geonote';
    bar.setAttribute('role', 'status');

    var msg = document.createElement('span');
    msg.className = 'dnr2-geonote__t';
    msg.textContent = text;
    bar.appendChild(msg);

    function dismiss() {
      bar.classList.remove('is-in');
      setTimeout(function () {
        if (bar.parentNode) bar.parentNode.removeChild(bar);
      }, 300);
    }

    if (ctaLabel) {
      var cta = document.createElement('button');
      cta.type = 'button';
      cta.className = 'dnr2-geonote__cta';
      cta.textContent = ctaLabel;
      cta.addEventListener('click', function () {
        dismiss();
        onAccept();
      });
      bar.appendChild(cta);
    }

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'dnr2-geonote__x';
    close.setAttribute('aria-label', 'Dismiss');
    close.innerHTML = '&#10005;';
    close.addEventListener('click', dismiss);
    bar.appendChild(close);

    document.body.appendChild(bar);
    requestAnimationFrame(function () {
      bar.classList.add('is-in');
    });
    setTimeout(dismiss, 14000);
  }

  function submitCountry(code) {
    var form = document.createElement('form');
    form.method = 'post';
    form.action = root() + 'localization';
    form.hidden = true;

    [
      ['form_type', 'localization'],
      ['utf8', '✓'],
      ['_method', 'put'],
      ['country_code', code],
      ['return_to', window.location.pathname + window.location.search]
    ].forEach(function (pair) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = pair[0];
      input.value = pair[1];
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  function detect(config) {
    var current = config.getAttribute('data-current');
    var offered = (config.getAttribute('data-available') || '').split(',');
    var mode = config.getAttribute('data-mode') || 'auto';

    fetch(root() + 'browsing_context_suggestions.json?country[enabled]=true&country[exclude]=' + encodeURIComponent(current))
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        if (!data || !data.suggestions || !data.suggestions.length) return;

        var parts = data.suggestions[0].parts || {};
        var suggested = parts.country && parts.country.handle;
        if (!suggested || suggested === current) return;
        /* A visitor in France is left on USD: we only move people between the
           four markets the selector offers, so detection can never strand
           someone on a currency they can't switch back out of. */
        if (offered.indexOf(suggested) === -1) return;

        if (mode === 'ask') {
          /* Remember that we asked, so someone who ignores it isn't asked
             again on every page. */
          writeState({ country: current, how: 'asked' });
          showNotice(
            fill(config.getAttribute('data-ask'), suggested),
            config.getAttribute('data-ask-cta'),
            function () {
              writeState({ country: suggested, how: 'user' });
              submitCountry(suggested);
            }
          );
          return;
        }

        writeState({ country: suggested, how: 'auto', notice: true });
        submitCountry(suggested);
      })
      .catch(function () {
        /* Detection is a nicety — never let it break the page. */
      });
  }

  function init() {
    var config = document.querySelector('[data-dnr-geo]');
    if (!config) return;

    var state = readState();

    /* Landing back here after an automatic switch: say what happened, once. */
    if (state && state.how === 'auto' && state.notice) {
      state.notice = false;
      writeState(state);
      if (state.country === config.getAttribute('data-current')) {
        showNotice(
          fill(config.getAttribute('data-notice'), state.country),
          config.getAttribute('data-notice-cta'),
          openSelector
        );
      }
      return;
    }

    if (state) return;
    detect(config);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
